'use client';

import { usePathname } from '@/i18n/navigation';
import { event_legacy_product_viewed, event_view_item } from '@/utils/gtm';
import { getItemId } from '@/utils/gtm/item-lists';
import { useLocale } from 'next-intl';
import { useEffect, useRef } from 'react';

// Spec: fire once per PDP view. A PDP re-renders whenever the cart, session or
// a lazy section settles, and React runs effects twice under StrictMode in
// development, so the last view is remembered and a repeat of it is ignored.
//
// Only an immediate repeat is suppressed. Moving A -> B -> A is three views and
// reports three times, which is what "once per PDP view" means.
let lastViewKey = null;

/**
 * Reports `view_item` as soon as the PDP has product data, and again when an
 * SPA navigation swaps in another product without unmounting the page.
 */
export default function useViewItem(product) {
  const locale = useLocale();
  const pathname = usePathname();
  const itemId = getItemId(product);

  const latest = useRef(null);
  latest.current = { product, locale };

  useEffect(() => {
    if (!itemId) return;

    const key = `${pathname}::${itemId}`;
    if (lastViewKey === key) return;
    lastViewKey = key;

    event_view_item(latest.current);
    // Kept firing alongside the spec event; see the legacy block in index.js.
    event_legacy_product_viewed({ product: latest.current.product });
  }, [itemId, pathname]);
}
